import React, { useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import Pagination from "react-js-pagination";
import moment from "moment";
import Header from "./../../Header";
import "../../assets/css/alie.css";

import SelectPicker from "rsuite/SelectPicker";
import "rsuite/SelectPicker/styles/index.css";

import InteractionsModal from "../chat/components/InteractionsModal";

const INTENT_OPTIONS = [
  { label: "PRODUCT RECOMMENDATION", value: "PRODUCT_RECOMMENDATION" },
  { label: "SUPPORT QUERY", value: "SUPPORT_QUERY" },
  { label: "GENERAL INQUIRY", value: "GENERAL_INQUIRY" },
  { label: "OFFENSIVE LANGUAGE", value: "OFFENSIVE_LANGUAGE" },
  { label: "OFF TOPIC BUT RELATED", value: "OFF_TOPIC_BUT_RELATED" },
  { label: "COMPLETELY OFF TOPIC", value: "COMPLETELY_OFF_TOPIC" },
  { label: "UNCLEAR INTENT", value: "UNCLEAR_INTENT" },
];

const FLAG_OPTIONS = [
  { label: "All", value: "all" },
  { label: "Downvoted", value: "was_down_voted" },
  { label: "Low Confidence", value: "low_confidence" },
  { label: "Web Search", value: "web_search_used" },
  { label: "Flagged", value: "flagged" },
  { label: "Bad Survey", value: "survey_rating_bad" },
];

const AlieResponseEval = () => {
  const [messages, setMessages] = useState([]);
  const [conversationSearch, setConversationSearch] = useState("");
  const [debouncedConversationSearch, setDebouncedConversationSearch] = useState("");
  const debounceTimeout = useRef(null);

  const [showInteractionsModal, setShowInteractionsModal] = useState(false);
  const [selectedInteractionsId, setSelectedInteractionsId] = useState(null);

  const location = useLocation();
  const [currentPage, setCurrentPage] = useState(
    location.state && location.state.page ? location.state.page : 1
  );

  const perPage = 20;
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);

  const [agents, setAgents] = useState([]);

  const [filters, setFilters] = useState({
    flag: "all",
    agent_id: null,
    intent_code: null,
  });

  const updateFilter = (key, value) => {
    setFilters((prev) => ({
      ...prev,
      [key]: value && value.length === 0 ? null : value,
    }));
    setCurrentPage(1);
  };

  const fetchAgents = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_CRM}/ai/agents`);
      const result = await response.json();
      setAgents(result.data || []);
    } catch (err) {
      console.error("Failed to fetch agents", err);
    }
  };

  const buildQueryString = (page) => {
    const params = new URLSearchParams();
    params.set("page", String(page));
    params.set("per_page", String(perPage));

    if (filters.agent_id) {
      params.append("agent_id", filters.agent_id);
    }

    if (filters.intent_code) {
      params.append("intent_code", filters.intent_code);
    }

    if (debouncedConversationSearch.trim()) {
      params.set("conversation_id", debouncedConversationSearch.trim());
    }

    switch (filters.flag) {
      case "was_down_voted":
        params.set("was_down_voted", "true");
        break;
      case "low_confidence":
        params.set("low_confidence", "true");
        break;
      case "web_search_used":
        params.set("web_search_used", "true");
        break;
      case "flagged":
        params.set("flagged", "true");
        break;
      case "survey_rating_bad":
        params.set("survey_rating_bad", "true");
        break;
      default:
        break;
    }

    return params.toString();
  };

  const fetchMessages = async (page) => {
    setLoading(true);
    try {
      const queryString = buildQueryString(page);
      const response = await fetch(
        `${process.env.REACT_APP_ALIE_API_URL}/alie/v1/evaluation/list?${queryString}`
      );

      const data = await response.json();
      setMessages(Array.isArray(data.data) ? data.data : []);
      setTotal(
        data && data.pagination && typeof data.pagination.total_items === "number"
          ? data.pagination.total_items
          : 0
      );
    } catch (error) {
      console.error("Error fetching messages:", error);
      setMessages([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  };

  const getFlagBadges = (flags) => {
    if (!flags || typeof flags !== "object") {
      return <span className="text-muted">No flags</span>;
    }

    const flagLabels = [
      ["was_down_voted", "Downvoted"],
      ["survey_rating_bad", "Bad Survey"],
      ["low_confidence", "Low Confidence"],
      ["web_search_used", "Web Search"],
      ["flagged", "Flagged"],
    ].filter(([key]) => Boolean(flags[key]));

    if (flagLabels.length === 0) {
      return <span className="text-muted">No flags</span>;
    }

    return flagLabels.map(([key, label]) => (
      <span
        key={key}
        className="badge mr-1"
        style={{
          margin: 5,
          background: "#fff",
          color: "#333",
          padding: "0.65rem",
          fontWeight: 500,
          border: "1px solid #bfbfbf",
          textTransform: "capitalize",
        }}
      >
        {label}
      </span>
    ));
  };

  const buildSourceLink = (message) => {
    if (message.customer_tattle_uuid) {
      return `/chat/${message.customer_tattle_uuid}${message.interaction_id ? `#${message.interaction_id}` : ""}`;
    }
    return `/alie?conversation_id=${message.conversation_id || ""}${message.interaction_id ? `#${message.interaction_id}` : ""}`;
  };

  const clipText = (value, limit = 70) => {
    if (!value) {
      return "—";
    }
    return value.length > limit ? `${value.slice(0, limit)}...` : value;
  };

  const getScoreMeta = (message) => {
    const score =
      typeof message.confidence_score === "number" ? message.confidence_score : null;

    let code = message.assessment_code || "";
    if (!code) {
      if (score === null) {
        code = "not_available";
      } else if (score < 0.6) {
        code = "low_confidence";
      } else if (score < 0.8) {
        code = "medium_confidence";
      } else {
        code = "high_confidence";
      }
    }

    const palette = {
      low_confidence: {
        label: "Needs Review",
        textColor: "#b42318",
        background: "#fef3f2",
        border: "#fecdca",
        fill: "#f04438",
      },
      medium_confidence: {
        label: "Watch",
        textColor: "#b54708",
        background: "#fffaeb",
        border: "#fedf89",
        fill: "#f79009",
      },
      high_confidence: {
        label: "Strong",
        textColor: "#027a48",
        background: "#ecfdf3",
        border: "#abefc6",
        fill: "#12b76a",
      },
      not_available: {
        label: "No Score",
        textColor: "#475467",
        background: "#f2f4f7",
        border: "#d0d5dd",
        fill: "#98a2b3",
      },
    };

    return palette[code] || palette.not_available;
  };

  const renderScore = (message) => {
    const meta = getScoreMeta(message);
    const scorePercent =
      typeof message.confidence_score === "number"
        ? Math.max(0, Math.min(100, Math.round(message.confidence_score * 100)))
        : null;

    return (
      <div>
        <div
          style={{
            width: "100%",
            height: "6px",
            borderRadius: "999px",
            background: "#eaecf0",
            overflow: "hidden",
            marginBottom: "6px",
          }}
        >
          <div
            style={{
              width: scorePercent === null ? "35%" : `${scorePercent}%`,
              height: "100%",
              background: meta.fill,
              borderRadius: "999px",
            }}
          />
        </div>
        <div className="text-muted" style={{ fontSize: "12px" }}>
          {scorePercent === null ? "Confidence: N/A" : `Confidence: ${scorePercent}%`}
        </div>
      </div>
    );
  };

  useEffect(() => {
    if (document.title) {
      document.title = "Alie | AI Eval";
    }

    fetchAgents();
  }, []);

  useEffect(() => {
    if (debounceTimeout.current) {
      clearTimeout(debounceTimeout.current);
    }

    debounceTimeout.current = setTimeout(() => {
      setDebouncedConversationSearch(conversationSearch);
      setCurrentPage(1);
    }, 400);

    return () => {
      if (debounceTimeout.current) {
        clearTimeout(debounceTimeout.current);
      }
    };
  }, [conversationSearch]);

  useEffect(() => {
    fetchMessages(currentPage);
  }, [currentPage, debouncedConversationSearch, filters]);

  return (
    <div>
      <Header brecum_name="AI" brecum_page="Eval" />
      <div className="right_col" style={{ height: "100vh", overflow: "hidden" }}>
        <div
          className="ticket_list"
          style={{
            position: "relative",
            marginTop: "65px",
            height: "100%",
            paddingBottom: "80px",
          }}
        >
          <div
            className="customer-wrap"
            style={{
              overflowY: "auto",
              overflowX: "hidden",
              height: "100%",
              marginTop: 0,
            }}
          >
            <div className="page-top">
              <div className="containerfull">
                <div className="row align-items-center">
                  <div className="col-md-12">
                    <div className="d-flex justify-content-between align-items-center">
                      <div className="d-flex align-items-center">
                        <h2>AI Eval</h2>
                        <span className="badge-blue">{total}</span>
                      </div>
                      <div>
                        <div className="d-flex">
                          <div className="ml-10 doc-dropdown">
                            <SelectPicker
                              data={agents.map((agent) => ({
                                label: agent.agent_name,
                                value: agent.agent_id,
                              }))}
                              value={filters.agent_id}
                              onChange={(value) => updateFilter("agent_id", value)}
                              placeholder="Agent"
                              style={{ width: 224 }}
                              cleanable
                              searchable={false}
                            />
                          </div>
                          <div className="ml-10 doc-dropdown">
                            <SelectPicker
                              data={INTENT_OPTIONS}
                              value={filters.intent_code}
                              onChange={(value) => updateFilter("intent_code", value)}
                              placeholder="Intent"
                              style={{ width: 200 }}
                              cleanable
                              searchable={false}
                            />
                          </div>
                          <div className="ml-10 doc-dropdown">
                            <SelectPicker
                              data={FLAG_OPTIONS}
                              value={filters.flag}
                              onChange={(value) => updateFilter("flag", value || "all")}
                              placeholder="Flag"
                              style={{ width: 200 }}
                              cleanable={false}
                              searchable={false}
                            />
                          </div>
                          <div className="ml-10 searchTicket emp_dept">
                            <input
                              type="text"
                              className="form-control input-f"
                              placeholder="Conversation ID"
                              autoComplete="off"
                              value={conversationSearch}
                              style={{ height: "36px" }}
                              onChange={(e) => setConversationSearch(e.target.value)}
                            />
                            <button type="button">
                              <svg width="18" height="19" viewBox="0 0 18 19" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M8.25 14.75C11.5637 14.75 14.25 12.0637 14.25 8.75C14.25 5.43629 11.5637 2.75 8.25 2.75C4.93629 2.75 2.25 5.43629 2.25 8.75C2.25 12.0637 4.93629 14.75 8.25 14.75Z" stroke="white" strokeWidth="1.33" strokeLinecap="round" strokeLinejoin="round"></path>
                                <path d="M15.75 16.25L12.4875 12.9875" stroke="white" strokeWidth="1.33" strokeLinecap="round" strokeLinejoin="round"></path>
                              </svg>
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {loading ? (
                  <div className="text-center" style={{ padding: "40px" }}>
                    <p>Loading...</p>
                  </div>
                ) : (
                  <div className="employee-list table-wrap mt-10">
                    <table>
                      <thead>
                        <tr>
                          <th style={{ width: "10%" }}>ID</th>
                          <th style={{ width: "14%" }}>Date</th>
                          <th style={{ width: "24%" }}>User Query</th>
                          <th style={{ width: "14%" }}>Agent Name</th>
                          <th style={{ width: "15%" }}>Flag</th>
                          <th style={{ width: "13%" }}>Confidence</th>
                          <th style={{ width: "18%" }}>Action</th>
                        </tr>
                      </thead>
                      <style>
                        {`
                          .important-link {
                            color: #007bff !important;
                            text-decoration: underline !important;
                          }
                          .important-link:hover {
                            color: #0056b3 !important;
                          }
                        `}
                      </style>
                      <tbody>
                        {messages.map((message) => (
                          <tr key={message.interaction_id || message.conversation_id}>
                            <td>
                              {message.eval_id ? (
                                <Link
                                  to={{
                                    pathname: `/eval/${message.eval_id}`,
                                    state: { page: currentPage },
                                  }}
                                  className="important-link"
                                >
                                  {message.eval_id}
                                </Link>
                              ) : (
                                "N/A"
                              )}
                            </td>
                            <td>{message.date_of_eval ? moment(message.date_of_eval).format("YYYY-MM-DD hh:mm A") : "—"}</td>
                            <td>
                              <a
                                href={buildSourceLink(message)}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="important-link"
                                title={message.user_query || ""}
                              >
                                {clipText(message.user_query)}
                              </a>
                            </td>
                            <td>{message.agent_name || message.agent_id || "—"}</td>
                            <td>{getFlagBadges(message.flags)}</td>
                            <td>{renderScore(message)}</td>
                            <td style={{ minWidth: "140px", whiteSpace: "nowrap" }}>
                              <div style={{ display: "flex", flexDirection: "column", gap: "8px", whiteSpace: "nowrap" }}>
                                {message.interaction_id ? (
                                  <div
                                    className="interactions-button"
                                    onClick={() => {
                                      setSelectedInteractionsId(message.interaction_id);
                                      setShowInteractionsModal(true);
                                    }}
                                    style={{ cursor: "pointer", color: "#007bff", whiteSpace: "nowrap", display: "inline-block" }}
                                  >
                                    View Source
                                  </div>
                                ) : null}
                              </div>
                            </td>
                          </tr>
                        ))}
                        {messages.length === 0 && (
                          <tr>
                            <td colSpan="8" className="text-center" style={{ padding: "30px" }}>
                              No evaluations found
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                    {total > perPage && (
                      <div className="d-flex mu-pagination justify-content-between align-items-center">
                        <div className="mu-pagination__info">
                          <p className="mu-pagination__info-text">
                            Showing {Math.min((currentPage - 1) * perPage + 1, total)} to{" "}
                            {Math.min(currentPage * perPage, total)} of {total} entries
                          </p>
                        </div>
                        <div className="mu-pagination">
                          <Pagination
                            activePage={currentPage}
                            itemsCountPerPage={perPage}
                            totalItemsCount={total}
                            pageRangeDisplayed={5}
                            itemClass="page-item"
                            linkClass="page-link"
                            onChange={setCurrentPage}
                          />
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
          <InteractionsModal
            interactionId={selectedInteractionsId}
            showModal={showInteractionsModal}
            setShowModal={setShowInteractionsModal}
          />
        </div>
      </div>
    </div>
  );
};

export default AlieResponseEval;



// WEBPACK FOOTER //
// ./src/components/ai/AlieResponseEval.js